
/// <amd-module name="Environments/CreatePanel/Components/AddResourcesComponent" />
import * as React from "react";
import { EnvironmentResourceType } from "DistributedTask/Client/Contracts/TaskAgent";

import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import * as Resources from "Environments/CreatePanel/Resources";
import { IButtonProps } from "VSSUI/Button";
import { getTitle, getResources } from "Environments/CreatePanel/Common/Utils";
import { EnvironmentBaseComponent, IEnvironmentBaseComponentProperties, IEnvironmentBaseComponentState } from "Environments/EnvironmentsCommon/EnvironmentBaseComponent";
import { Panel } from "VSSUI/Panel";
import { Page } from "VSSUI/Page";
import { SelectResourceComponent } from "Environments/CreatePanel/Components/SelectResourceComponent";
import { AddResourceComponent } from "Environments/CreatePanel/Components/AddResourceComponent";
import { IVssHistoryService } from "VSS/Platform/History";
import { UrlParamKeys } from "Environments/EnvironmentsCommon/Utils";
import { createUrlForRoute } from "Environments/EnvironmentsCommon/Link";

interface IAddResourcesState extends IEnvironmentBaseComponentState {
    environmentName?: string;
    environmentDescription?: string;
    resourceType: EnvironmentResourceType;
    disableActionButton: boolean;
    isAddResourceView?: boolean;
    environmentId: number | undefined | null;
    createResourceSignal: boolean;
}

interface IAddResourcesProperties extends IEnvironmentBaseComponentProperties {
    onResourceCreated: () => void;
    onDismissPanel: () => void;
    instanceId: string;
    environmentId: number;
    environmentName?: string;
}

export class AddResourcesComponent extends EnvironmentBaseComponent<IAddResourcesProperties, IAddResourcesState> {

    public getInstanceId(context: IVssComponentContext): string {
        return this.props.instanceId;
    }

    constructor(props: IAddResourcesProperties, context: IVssComponentContext) {
        super(props, context);
        const historyService = context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const historyState = historyService.getState();
        const typeFromUrl = historyState[UrlParamKeys.CreateNew] && parseInt(historyState[UrlParamKeys.CreateNew]);
        const type = typeFromUrl || this._resources[0].resourceType;

        delete historyState[UrlParamKeys.CreateNew];
        historyService.replaceState(historyState);

        this.state = {
            errorMessages: {},
            disableActionButton: false,
            environmentId: undefined,
            createResourceSignal: false,
            isAddResourceView: typeFromUrl > EnvironmentResourceType.Undefined,
            resourceType: type
        };
    }

    public render(): JSX.Element {
        if (this.state.isAddResourceView) {
            const queryParams: { [s: string]: string } = this.props.environmentId ? { environmentId: this.props.environmentId.toString() } : {};
            if (this.state.resourceType) {
                queryParams[UrlParamKeys.CreateNew] = (this.state.resourceType as number).toString()
            }

            // createUrlForRoute will give us the relative path. Add the domain to that
            let urlToCurrentView = createUrlForRoute(this.context.pageContext, "ms.vss-environments-web.environments-route-with-id", queryParams);
            if (urlToCurrentView[0] === '/') {
                urlToCurrentView = window.location.origin + urlToCurrentView;
            }

            return (
                <AddResourceComponent
                    onResourceCreated={this.props.onResourceCreated}
                    onResourceCreationFailed={this._onResourceCreationFailed}
                    onDismissPanel={this.props.onDismissPanel}
                    onBackButtonClick={this._onBackButtonClick}
                    instanceId={this._instanceId}
                    onCreateButtonClick={this._onCreateButtonClick}
                    resourceType={this.state.resourceType}
                    environmentId={this.props.environmentId}
                    environmentName={this.props.environmentName}
                    isAddEnvironmentView={false}
                    createResourceSignal={this.state.createResourceSignal}
                    urlToCurrentView={urlToCurrentView}
                />
            );
        } else {
            return (
                <Panel titleProps={getTitle(Resources.AddResource)} onDismiss={this.props.onDismissPanel} footerButtonProps={this._getButtonProps()}>
                    <div className="panel-content flex-grow">
                        <SelectResourceComponent onSelectionResourceType={this._onSelectionResourceType} selectedResourceType={this._getSelectedIndex()} resources={this._resources} requiresFocus={true} />
                    </div>
                </Panel>
            );
        }
    }

    private _getSelectedIndex(): string | undefined {
        return this.state.resourceType.toString();
    }

    private _getButtonProps(): IButtonProps[] {
        let buttonsProps: IButtonProps[] = [];
        buttonsProps.push({
            ariaLabel: Resources.NextButtonText,
            onClick: this._nextButtonClick,
            text: Resources.NextButtonText,
            primary: true,
            disabled: this.state.disableActionButton
        });
        return buttonsProps;
    }

    private _nextButtonClick = () => {
        this.setState({ isAddResourceView: true });
    }

    private _onBackButtonClick = () => {
        this.setState({ isAddResourceView: false });
    }

    private _onCreateButtonClick = () => {
        this.setState({ createResourceSignal: true });
    }

    private _onSelectionResourceType = (resourceType: EnvironmentResourceType): void => {
        this.setState({ resourceType: resourceType, disableActionButton: !resourceType });
    }

    private _onResourceCreationFailed = (): void => {
        this.setState({ createResourceSignal: false });
    }

    private _resources = getResources(this.context.pageContext).filter(value => value.resourceType !== EnvironmentResourceType.Undefined);
}

VssComponent.register("addResources", AddResourcesComponent);
